import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthStore, getAccessToken, getTenantId, type AuthUser } from '../store/authStore';

// ── Types ──────────────────────────────────────────────────────────────────

export type UserRole = 'SUPERADMIN' | 'ADMIN' | 'DATA_ENGINEER' | 'ANALYST' | 'VIEWER';

export interface MaicUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  password: string;
  createdAt: string;
  active: boolean;
  mustChangePassword?: boolean;
  createdBy?: string;
  /** If set, user can only see these module IDs. Null/empty = all modules. ADMINs always see all. */
  allowed_modules?: string[];
  /** Whether the account has TOTP MFA enrolled. */
  mfa_enabled?: boolean;
  /** Which authority performed this session's second factor ('idp' = the
   *  customer's identity provider, under a delegated SSO link). Drives the
   *  enrollment interstitial only; see lib/mfaPolicy.ts. */
  mfa_source?: 'idp' | 'totp';
  /** Whether the account has a local password (false for SSO-created accounts). */
  has_password?: boolean;
  /** Set to the impersonating admin's email when this session is an active impersonation. */
  impersonated_by?: string;
  /** Tenant the session belongs to (from the JWT). */
  tenant_id?: string;
}

interface Tenant {
  id: string;
  name: string;
  plan: 'starter' | 'professional' | 'enterprise';
}

interface AuthContextValue {
  tenant: Tenant;
  currentUser: MaicUser | null;
  users: MaicUser[];
  isAuthenticated: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  /** The signed-in person replaces their own password (the temporary one an
   *  administrator set, typically). Resolves with the server's detail on failure. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  /** Reflect a just-completed MFA enrollment for the logged-in user. */
  setMfaEnabled: (enabled: boolean) => void;
  addUser: (data: Omit<MaicUser, 'id' | 'createdAt'>) => Promise<MaicUser>;
  /** Resolves once the server answered; never rejects. `ok: false` carries the
   *  server's reason (the local list is already restored by then). */
  updateUser: (id: string, patch: Partial<MaicUser>) => Promise<{ ok: boolean; error?: string }>;
  deleteUser: (id: string) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────

const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';

// ── Role mapping ──────────────────────────────────────────────────────────

function mapRole(role: string): UserRole {
  switch (role) {
    case 'superadmin': return 'SUPERADMIN';
    case 'admin': return 'ADMIN';
    case 'analyst': return 'ANALYST';
    case 'viewer': return 'VIEWER';
    default: return 'VIEWER';
  }
}

/** The shell's view of the signed-in user, derived from the auth store. */
export function toMaicUser(u: AuthUser): MaicUser {
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    role: mapRole(u.role),
    password: '',
    createdAt: new Date().toISOString(),
    active: true,
    mustChangePassword: !!u.must_change_password,
    mfa_enabled: !!u.mfa_enabled,
    mfa_source: u.mfa_source,
    has_password: u.has_password,
    impersonated_by: u.impersonated_by,
    tenant_id: u.tenant_id,
  };
}

function mapRoleToApi(role: UserRole): string {
  switch (role) {
    case 'SUPERADMIN': return 'superadmin';
    case 'ADMIN': return 'admin';
    case 'DATA_ENGINEER': return 'analyst';
    case 'ANALYST': return 'analyst';
    case 'VIEWER': return 'viewer';
  }
}

function mapApiUser(u: Record<string, unknown>): MaicUser {
  return {
    id: u.id as string,
    name: (u.name as string) || (u.email as string),
    email: u.email as string,
    role: mapRole(u.role as string),
    password: '',
    createdAt: (u.created_at as string) || new Date().toISOString(),
    active: (u.is_active as boolean) ?? true,
    mustChangePassword: !!u.must_change_password,
    mfa_enabled: !!u.mfa_enabled,
  };
}

// ── Auth fetch helper ─────────────────────────────────────────────────────

function authHeaders(json = true): Record<string, string> {
  const token = getAccessToken();
  const tenantId = getTenantId();
  const h: Record<string, string> = { 'x-tenant-id': tenantId };
  if (json) h['Content-Type'] = 'application/json';
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function fetchUsersFromApi(tenantId: string): Promise<MaicUser[]> {
  try {
    const token = getAccessToken();
    const res = await fetch(`${AUTH_API}/auth/users`, {
      headers: {
        'x-tenant-id': tenantId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.users || []).map(mapApiUser);
  } catch {
    return [];
  }
}

// ── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
  tenant: { id: 'tenant-001', name: 'maic', plan: 'enterprise' },
  currentUser: null,
  users: [],
  isAuthenticated: false,
  login: async () => ({ success: false }),
  logout: () => {},
  changePassword: async () => ({ success: false }),
  setMfaEnabled: () => {},
  addUser: async () => ({ id: '', name: '', email: '', role: 'VIEWER', password: '', createdAt: '', active: true }),
  updateUser: async () => ({ ok: false }),
  deleteUser: () => {},
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authState = useAuthStore();
  const [users, setUsers] = useState<MaicUser[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Derive currentUser from authStore
  const currentUser: MaicUser | null = authState.user ? toMaicUser(authState.user) : null;

  const tenantId = authState.user?.tenant_id || 'tenant-001';
  const tenant: Tenant = { id: tenantId, name: tenantId, plan: 'enterprise' };

  // On mount: try to restore session via refresh token cookie
  // Skip refresh if we have an impersonation token — it would overwrite the impersonated session
  useEffect(() => {
    if (sessionStorage.getItem('_nexus_impersonation_token')) {
      setLoaded(true);
    } else {
      authState.refresh().then(() => setLoaded(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When user changes (login → new tenant, or logout), reload users list
  useEffect(() => {
    if (authState.user) {
      fetchUsersFromApi(authState.user.tenant_id).then(setUsers);
    } else {
      setUsers([]);
    }
  }, [authState.user?.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string, totpCode?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await authState.login(email, password, undefined, totpCode);
      return { success: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Invalid credentials';
      return { success: false, error: msg };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    authState.logout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      await authState.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : 'Failed to change password' };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setMfaEnabled = useCallback((enabled: boolean) => {
    authState.setMfaEnabled(enabled);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Optimistic: return immediately so caller can show credentials
  const addUser = useCallback(async (data: Omit<MaicUser, 'id' | 'createdAt'>): Promise<MaicUser> => {
    // Optimistic: show the row immediately while the request is in flight.
    const tempUser: MaicUser = {
      ...data,
      id: `temp-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setUsers((prev) => [...prev, tempUser]);

    try {
      const res = await fetch(`${AUTH_API}/auth/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          email: data.email,
          name: data.name,
          role: mapRoleToApi(data.role),
          password: data.password || undefined,
          allowed_modules: data.allowed_modules ?? null,
        }),
      });

      // A non-2xx response is NOT a network error, so it would otherwise slip
      // past .catch and get mapped as if it were a user (yielding a row with no
      // name/email). Treat it as a failure and surface the server's detail.
      if (!res.ok) {
        let detail = `Failed to create user (${res.status})`;
        try {
          const err = await res.json();
          if (typeof err?.detail === 'string') detail = err.detail;
        } catch { /* response had no JSON body */ }
        throw new Error(detail);
      }

      const created = await res.json();
      const mapped = mapApiUser(created);
      setUsers((prev) => prev.map((u) => u.id === tempUser.id ? mapped : u));
      return mapped;
    } catch (e) {
      // Roll back the optimistic row on any failure (network or HTTP error).
      setUsers((prev) => prev.filter((u) => u.id !== tempUser.id));
      throw e instanceof Error ? e : new Error('Failed to create user. Please try again.');
    }
  }, []);

  const updateUser = useCallback(async (id: string, patch: Partial<MaicUser>): Promise<{ ok: boolean; error?: string }> => {
    setUsers((prev) => prev.map((u) => u.id === id ? { ...u, ...patch } : u));

    const body: Record<string, unknown> = {};
    if (patch.name !== undefined) body.name = patch.name;
    if (patch.role !== undefined) body.role = mapRoleToApi(patch.role as UserRole);
    if (patch.active !== undefined) body.is_active = patch.active;
    if (patch.password) body.password = patch.password;
    if ('allowed_modules' in patch) body.allowed_modules = patch.allowed_modules ?? null;

    try {
      const res = await fetch(`${AUTH_API}/auth/users/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        // The server decides what is temporary (a reset by someone else): take
        // its answer instead of the optimistic guess.
        const saved = mapApiUser(await res.json().catch(() => ({ id })));
        if (saved.email) setUsers((prev) => prev.map((u) => u.id === id ? { ...u, mustChangePassword: saved.mustChangePassword } : u));
        return { ok: true };
      }
      // A refused change (400 weak password, 403 rank, 409: the tenant's last
      // admin, your own account) must not stay applied in the list, and the
      // caller must be able to say so: a reset that silently did not happen
      // leaves a temporary password nobody can sign in with.
      let detail = `Failed to update user (${res.status})`;
      try {
        const err = await res.json();
        if (typeof err?.detail === 'string') detail = err.detail;
      } catch { /* no JSON body */ }
      fetchUsersFromApi(tenantId).then(setUsers);
      return { ok: false, error: detail };
    } catch {
      fetchUsersFromApi(tenantId).then(setUsers);
      return { ok: false, error: 'Network error' };
    }
  }, [tenantId]);

  const deleteUser = useCallback((id: string) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    fetch(`${AUTH_API}/auth/users/${id}`, {
      method: 'DELETE',
      headers: authHeaders(false),
    }).then((res) => {
      if (!res.ok) fetchUsersFromApi(tenantId).then(setUsers);
    }).catch(() => {
      fetchUsersFromApi(tenantId).then(setUsers);
    });
  }, [tenantId]);

  if (!loaded) return null;

  return (
    <AuthContext.Provider value={{
      tenant,
      currentUser,
      users,
      isAuthenticated: currentUser !== null,
      login,
      logout,
      changePassword,
      setMfaEnabled,
      addUser,
      updateUser,
      deleteUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useTenant = () => useContext(AuthContext);
export const useUser = () => useContext(AuthContext).currentUser;
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
